import { LOCALES, type Locale } from "./types";
import {
  allGuidePaths,
  guideGroupForPath,
  guidePathForGroup,
} from "@/content/guides/registry";

export type PublicRouteKey =
  | "home"
  | "privacy"
  | "terms"
  | "cookies"
  | "dataDeletion";

export type AppRouteKey =
  | "dashboard"
  | "dashboardReports"
  | "dashboardCredits"
  | "reportDetail";

export type LocalePaths = Record<Locale, string>;

export const PUBLIC_ROUTES: Record<PublicRouteKey, LocalePaths> = {
  home: { sl: "/", en: "/en", de: "/de", ru: "/ru" },
  privacy: {
    sl: "/varovanje-podatkov",
    en: "/en/privacy-policy",
    de: "/de/datenschutz",
    ru: "/ru/konfidencialnost",
  },
  terms: {
    sl: "/splosni-pogoji",
    en: "/en/terms-of-service",
    de: "/de/agb",
    ru: "/ru/usloviya",
  },
  cookies: {
    sl: "/piskotki",
    en: "/en/cookie-policy",
    de: "/de/cookies",
    ru: "/ru/cookies",
  },
  dataDeletion: {
    sl: "/brisanje-podatkov",
    en: "/en/data-deletion",
    de: "/de/datenloeschung",
    ru: "/ru/udalenie-dannyh",
  },
};

export const APP_ROUTES: Record<AppRouteKey, LocalePaths> = {
  dashboard: {
    sl: "/nadzorna-plosca",
    en: "/en/dashboard",
    de: "/de/dashboard",
    ru: "/ru/dashboard",
  },
  dashboardReports: {
    sl: "/nadzorna-plosca/porocila",
    en: "/en/dashboard/reports",
    de: "/de/dashboard/berichte",
    ru: "/ru/dashboard/otchety",
  },
  dashboardCredits: {
    sl: "/nadzorna-plosca/krediti",
    en: "/en/dashboard/credits",
    de: "/de/dashboard/guthaben",
    ru: "/ru/dashboard/kredity",
  },
  reportDetail: {
    sl: "/nadzorna-plosca/porocilo",
    en: "/en/dashboard/report",
    de: "/de/dashboard/bericht",
    ru: "/ru/dashboard/otchet",
  },
};

const PATH_TO_KEY = new Map<string, PublicRouteKey>();
for (const [key, paths] of Object.entries(PUBLIC_ROUTES) as [
  PublicRouteKey,
  LocalePaths,
][]) {
  for (const path of Object.values(paths)) {
    PATH_TO_KEY.set(path, key);
  }
}

export function localeFromPath(pathname: string): Locale {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  if (pathname === "/de" || pathname.startsWith("/de/")) return "de";
  if (pathname === "/ru" || pathname.startsWith("/ru/")) return "ru";
  return "sl";
}

export function localizedPath(key: PublicRouteKey, locale: Locale): string {
  return PUBLIC_ROUTES[key][locale];
}

export function dashboardPath(locale: Locale): string {
  return APP_ROUTES.dashboard[locale];
}

export function dashboardReportsPath(locale: Locale): string {
  return APP_ROUTES.dashboardReports[locale];
}

export function dashboardCreditsPath(locale: Locale): string {
  return APP_ROUTES.dashboardCredits[locale];
}

/** Localized child segments for the nested dashboard layout routes. */
export const DASHBOARD_ROUTE_SEGMENTS: Array<{
  base: string;
  reports: string;
  credits: string;
  detail: string;
}> = [
  { base: "/nadzorna-plosca", reports: "porocila", credits: "krediti", detail: "porocilo/:orderId" },
  { base: "/en/dashboard", reports: "reports", credits: "credits", detail: "report/:orderId" },
  { base: "/de/dashboard", reports: "berichte", credits: "guthaben", detail: "bericht/:orderId" },
  { base: "/ru/dashboard", reports: "otchety", credits: "kredity", detail: "otchet/:orderId" },
];

export function reportDetailPath(locale: Locale, orderId: string): string {
  return `${APP_ROUTES.reportDetail[locale]}/${orderId}`;
}

/** Checkout keeps its locale in the URL, including reloads and shared links. */
export function checkoutPath(locale: Locale): string {
  return locale === "sl" ? "/checkout" : `/${locale}/checkout`;
}

export const CHECKOUT_PATHS = LOCALES.map(checkoutPath);

/**
 * Where Stripe returns a buyer. The unprefixed path is Slovenian; the others
 * keep an /en, /de or /ru buyer in their language on the success screen.
 */
export function paymentSuccessPath(locale: Locale): string {
  return locale === "sl" ? "/payment-success" : `/${locale}/payment-success`;
}

export function isDashboardPath(pathname: string): boolean {
  if (pathname === "/dashboard") return true;
  // Prefix match so dashboard sub-pages (reports/credits) + report detail count.
  return Object.values(APP_ROUTES.dashboard).some(
    (base) => pathname === base || pathname.startsWith(`${base}/`)
  );
}

// Exact localized path -> app route key, for the index-like dashboard pages.
const APP_PATH_TO_KEY = new Map<string, AppRouteKey>();
for (const key of ["dashboard", "dashboardReports", "dashboardCredits"] as AppRouteKey[]) {
  for (const path of Object.values(APP_ROUTES[key])) {
    APP_PATH_TO_KEY.set(path, key);
  }
}

export function isReportDetailPath(pathname: string): boolean {
  return Object.values(APP_ROUTES.reportDetail).some((prefix) =>
    pathname.startsWith(`${prefix}/`)
  );
}

/** Dashboard, report detail, checkout, payment, and admin routes. */
export function isAppAreaPath(pathname: string): boolean {
  return (
    isDashboardPath(pathname) ||
    isReportDetailPath(pathname) ||
    pathname.startsWith("/admin") ||
    CHECKOUT_PATHS.includes(pathname) ||
    /^\/(?:(?:en|de|ru)\/)?payment-/.test(pathname)
  );
}

export function extractReportOrderId(pathname: string): string | null {
  for (const prefix of Object.values(APP_ROUTES.reportDetail)) {
    if (pathname.startsWith(`${prefix}/`)) {
      return pathname.slice(`${prefix}/`.length) || null;
    }
  }
  return null;
}

export function switchLocalePath(pathname: string, targetLocale: Locale): string {
  if (CHECKOUT_PATHS.includes(pathname)) return checkoutPath(targetLocale);
  const routeKey = PATH_TO_KEY.get(pathname);
  if (routeKey) {
    return PUBLIC_ROUTES[routeKey][targetLocale];
  }
  // Guide hub / articles: map to the same guide in the target locale, or fall
  // back to home if that translation does not exist yet (e.g. de/ru).
  const guideGroup = guideGroupForPath(pathname);
  if (guideGroup) {
    return (
      guidePathForGroup(guideGroup, targetLocale) ??
      localizedPath("home", targetLocale)
    );
  }
  // Dashboard index / reports / credits map to the matching sub-page.
  const appKey = APP_PATH_TO_KEY.get(pathname);
  if (appKey) {
    return APP_ROUTES[appKey][targetLocale];
  }
  const reportOrderId = extractReportOrderId(pathname);
  if (reportOrderId) {
    return reportDetailPath(targetLocale, reportOrderId);
  }
  if (isDashboardPath(pathname)) {
    return dashboardPath(targetLocale);
  }
  return localizedPath("home", targetLocale);
}

function prerenderOutput(path: string): string {
  if (path === "/") return "index.html";
  if (path === "/en") return "en/index.html";
  if (path === "/de") return "de/index.html";
  if (path === "/ru") return "ru/index.html";
  if (path.startsWith("/en/")) return `en${path.slice(3)}.html`;
  if (path.startsWith("/de/")) return `de${path.slice(3)}.html`;
  if (path.startsWith("/ru/")) return `ru${path.slice(3)}.html`;
  return `${path.slice(1)}.html`;
}

export const SSR_PRERENDER_ROUTES = (
  Object.values(PUBLIC_ROUTES) as LocalePaths[]
).flatMap((paths) =>
  Object.values(paths).map((path) => ({
    path,
    output: prerenderOutput(path),
  }))
);

export const HYDRATE_PATHS = new Set([
  ...SSR_PRERENDER_ROUTES.map((route) => route.path),
  ...allGuidePaths(),
]);
